import TopBar from '@/components/feature/TopBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation, useStaggeredAnimation, useParallax } from '@/hooks/useScrollAnimation';

const allServices = [
  { id: 1, title: 'Ginecología', description: 'Salud integral de la mujer, atención prenatal y prevención.', icon: 'ri-women-line' },
  { id: 2, title: 'Urología', description: 'Diagnóstico y tratamiento de patologías del sistema urinario.', icon: 'ri-test-tube-line' },
  { id: 3, title: 'Cirugía Cardiovascular', description: 'Procedimientos especializados de corazón y vasos sanguíneos.', icon: 'ri-heart-pulse-line' },
  { id: 4, title: 'Pediatría', description: 'Cuidado de niños y adolescentes, con atención neonatal.', icon: 'ri-bear-smile-line' },
  { id: 5, title: 'Nefrología', description: 'Tratamiento de enfermedades del riñón y diálisis.', icon: 'ri-drop-line' },
  { id: 6, title: 'Neumología', description: 'Atención de asma, EPOC y trastornos pulmonares.', icon: 'ri-lungs-line' },
  { id: 7, title: 'Cirugía General', description: 'Cirugía ambulatoria y mayor con seguimiento postoperatorio.', icon: 'ri-surgical-mask-line' },
  { id: 8, title: 'Traumatología', description: 'Manejo de fracturas, esguinces y cirugía ortopédica.', icon: 'ri-first-aid-kit-line' },
  { id: 9, title: 'Reumatología', description: 'Artritis, lupus, osteoporosis y enfermedades autoinmunes.', icon: 'ri-body-scan-line' },
  { id: 10, title: 'Oftalmología', description: 'Exámenes de visión, cataratas, glaucoma y refracción.', icon: 'ri-eye-line' },
  { id: 11, title: 'Neurología', description: 'Migrañas, epilepsia, ACV y enfermedades neurodegenerativas.', icon: 'ri-brain-line' },
  { id: 12, title: 'Gastroenterología', description: 'Endoscopias, colonoscopias y trastornos digestivos.', icon: 'ri-capsule-line' },
  { id: 13, title: 'Cirugía Pediátrica', description: 'Cirugía neonatal y corrección de malformaciones congénitas.', icon: 'ri-empathize-line' },
];

const joinBenefits = [
  { icon: 'ri-building-line', title: 'Consultorio gratuito', desc: 'Sin costo de alquiler mensual' },
  { icon: 'ri-time-line', title: 'Horarios flexibles', desc: 'Tú decides cuándo atender' },
  { icon: 'ri-team-line', title: 'Equipo multidisciplinario', desc: 'Colaboración entre especialistas' },
  { icon: 'ri-user-heart-line', title: '+5000 pacientes', desc: 'Base de pacientes establecida' },
];

export default function ServicesPage() {
  const heroAnim = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible, getDelay } = useStaggeredAnimation();
  const ctaAnim = useScrollAnimation();
  const emgBg = useParallax(0.2);

  return (
    <>
      <TopBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section ref={heroAnim.ref} className="relative overflow-hidden bg-background-100 py-20 md:py-28 px-4 md:px-8 lg:px-12">
          <div className="absolute inset-0 texture-grid opacity-60" />
          <div className="absolute -top-16 -right-16 w-96 h-96 bg-primary-100/50 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-accent-200/40 rounded-full blur-3xl" />
          <div className="relative max-w-4xl mx-auto text-center">
            <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50 text-primary-600 text-xs font-bold uppercase tracking-[0.15em] mb-6 border border-primary-200 reveal ${heroAnim.isVisible ? 'is-in' : ''}`}>
              <span className="w-2 h-2 rounded-full bg-accent-500" />
              Especialidades Médicas
            </span>
            <h1 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-foreground-950 mb-6 leading-[1.05] reveal ${heroAnim.isVisible ? 'is-in' : ''}`}>
              Servicios para toda <span className="italic text-primary-600">tu familia</span>
            </h1>
            <p className={`text-foreground-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed reveal ${heroAnim.isVisible ? 'is-in' : ''}`}>
              Una amplia gama de especialidades con profesionales altamente calificados. Consultorios disponibles las 24 horas.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section ref={gridRef} className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {allServices.map((service, i) => (
                <div
                  key={service.id}
                  className={`group bg-background-100 border border-background-200 rounded-3xl p-6 md:p-7 hover:border-primary-300 hover:bg-background-50 transition-all duration-500 reveal ${gridVisible ? 'is-in' : ''}`}
                  style={{ transitionDelay: getDelay(i % 3) }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500 mb-5 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                    <i className={`${service.icon} text-2xl`} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Join banner — dark */}
        <section ref={ctaAnim.ref} className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-secondary-950 relative overflow-hidden">
          <div className="absolute inset-0 texture-dots-light opacity-25" />
          <div className="absolute -top-20 right-10 w-80 h-80 rounded-full bg-primary-600/20 blur-3xl" />
          <div className="relative max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">
              <div className={`lg:col-span-6 reveal-left ${ctaAnim.isVisible ? 'is-in' : ''}`}>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-bold uppercase tracking-[0.15em] mb-6">
                  <span className="w-2 h-2 rounded-full bg-accent-500" />
                  Para médicos especialistas
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium text-background-50 mb-5 leading-[1.08]">
                  ¿Eres médico? <span className="italic text-accent-500">Trabaja con nosotros</span>
                </h2>
                <p className="text-background-200 leading-relaxed mb-4">
                  En Vitalmedic ofrecemos consultorios gratuitos para médicos especialistas. No pagas alquiler, solo un pequeño porcentaje de la consulta.
                </p>
                <p className="text-background-300 leading-relaxed mb-8">
                  Únete en especialidades como Ginecología, Urología, Cirugía Cardiovascular, Pediatría, Nefrología, Neumología y más.
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                  <a
                    href="https://wa.me/59168682033"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-whatsapp-line" />
                    </span>
                    Contactar al 68682033
                  </a>
                  <a
                    href="tel:+59177173439"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-background-50/10 border border-background-50/30 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line" />
                    </span>
                    Llamar al 77173439
                  </a>
                </div>
              </div>

              <div className={`lg:col-span-6 grid grid-cols-2 gap-4 reveal-right ${ctaAnim.isVisible ? 'is-in' : ''}`}>
                {joinBenefits.map((item, i) => (
                  <div key={item.title} className={`rounded-3xl bg-background-50/[0.04] border border-background-50/10 p-5 md:p-6 ${i % 2 === 1 ? 'mt-6' : ''}`}>
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-500 text-secondary-950 mb-4">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-background-50 mb-1">{item.title}</h3>
                    <p className="text-xs text-background-300">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Emergency with parallax */}
        <section className="px-4 md:px-8 lg:px-12 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="relative rounded-[2.5rem] overflow-hidden">
              <div className="absolute inset-0 overflow-hidden">
                <div ref={emgBg.ref} className="absolute inset-x-0 -top-16 -bottom-16" style={{ transform: `translateY(${emgBg.offset}px)` }}>
                  <img
                    src="https://static.readdy.ai/image/5774bb29582c40f5252e7d37d3c89ae3/796f6aa30fdfbf512ac61fb077a00eb3.png"
                    alt="Clínica Tarija Vitalmedic"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/95 via-secondary-950/80 to-secondary-950/50" />
              </div>
              <div className="relative px-7 md:px-16 py-16 md:py-20 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 mb-5">
                  <i className="ri-heart-pulse-line text-2xl" />
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-medium text-background-50 mb-4">
                  Emergencias las <span className="italic text-accent-500">24 horas</span>
                </h2>
                <p className="text-background-200 text-base mb-8 max-w-xl mx-auto">
                  Si necesitas atención inmediata, nuestro servicio de emergencias está disponible en todo momento.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="tel:+59177173439"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line" />
                    </span>
                    Llamar al 77173439
                  </a>
                  <a
                    href="https://wa.me/59177173439"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-background-50/10 border border-background-50/30 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-whatsapp-line" />
                    </span>
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}